import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Droplets, Layers, Sparkles, Hammer, ShieldCheck, Pipette } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const designHighlights = [
  {
    icon: Droplets,
    title: "Fugenlose Badsanierung (Mikrozement)",
    text: "Wir beschichten vorhandene Fliesen direkt oder gestalten Neubauten von Grund auf neu.",
    details: [
      "Keine Abrissarbeiten nötig, geringe Aufbauhöhe, absolut wasserdicht",
      "Optik: Von samtweichem Stein-Look bis hin zu markanten Strukturen"
    ]
  },
  {
    icon: Layers,
    title: "Beton- & Industrial-Optik",
    text: "Der Trend für moderne Wohnräume und Küchenrückwände.",
    details: [
      "Charakter: Puristisch, zeitlos und extrem robust",
      "Einsatz: Akzentwände im Wohnzimmer, Kaminumrandungen oder Flure"
    ]
  },
  {
    icon: Sparkles,
    title: "Edle Spachteltechniken (Kalk-Marmor)",
    text: "Italienisches Flair nach Herford. Mineralische Kalk-Marmorspachtel – optisch ein Highlight und hochgradig diffusionsoffen.",
    details: [
      "Natürlicher Glanz und gesundes Raumklima durch Feuchtigkeitsregulierung"
    ]
  },
  {
    icon: Hammer,
    title: "Metall- & Rostoptiken",
    text: "Oberflächen mit echtem Metallcharakter – Kupfer, Bronze oder Eisen.",
    details: [
      "Jede Wand wird durch unsere Handschrift zu einem handgefertigten Einzelstück"
    ]
  }
];

const qualityPoints = [
  {
    icon: ShieldCheck,
    title: "Abdichtung nach Norm",
    text: "Besonders im Duschbereich nutzen wir zertifizierte Abdichtungssysteme (DIN 18534)."
  },
  {
    icon: Layers,
    title: "Mehrschichtiger Aufbau",
    text: "Grundierung, Gewebearmierung, mehrere Spachtelgänge und eine finale Hochleistungsversiegelung."
  },
  {
    icon: Pipette,
    title: "Farbechtheit",
    text: "Wir mischen Pigmente exakt nach Ihren Wünschen für ein harmonisches Gesamtbild."
  }
];

const faqs = [
  {
    question: "Kann man wirklich über alte Fliesen spachteln?",
    answer: "Ja! Solange die Fliesen fest sitzen, bereiten wir den Untergrund so vor, dass wir direkt darüber beschichten können. Das spart Dreck, Lärm und Kosten für den Abriss."
  },
  {
    question: "Ist die Oberfläche rutschfest?",
    answer: "Absolut. Durch die Wahl der Versiegelung stellen wir sicher, dass Ihr Boden im Bad trittsicher ist, selbst bei Nässe."
  },
  {
    question: "Wie pflegt man ein fugenloses Bad?",
    answer: "Ganz einfach mit milden Reinigern. Da es keine rauen Fugen gibt, reicht meist ein Wischer aus, um die Fläche sauber zu halten."
  }
];

const Badsanierung = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Fugenlose Badsanierung & Exklusive Oberflächen",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Malerfachbetrieb Stein GmbH und Co. KG",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mindener Str. 183",
        "addressLocality": "Herford",
        "postalCode": "32049",
        "addressRegion": "NRW",
        "addressCountry": "DE"
      },
      "telephone": "+49-5221-3832130"
    },
    "areaServed": [
      { "@type": "City", "name": "Herford" },
      { "@type": "City", "name": "Hiddenhausen" },
      { "@type": "City", "name": "Löhne" }
    ],
    "description": "Fugenlose Bäder und exklusive Oberflächen in Herford: Mikrozement, Betonoptik, Kalk-Marmorspachtel und Metalloptiken vom Meisterbetrieb."
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Fugenlose Bäder in Herford: Hygienisch & pflegeleicht"
        description="Fugenlose Bäder in Herford: Mikrozement, Betonoptik & edle Spachteltechniken – hygienisch, pflegeleicht und individuell. ☎ 05221 3832130"
        keywords="fugenloses Bad Herford, Mikrozement Herford, Betonoptik Bad Herford, Badsanierung fugenlos, Kalk-Marmorspachtel Bad, Metalloptik Wand"
        canonical="https://malerfachbetrieb-stein.de/fugenlose-baeder-oberflaechen"
        structuredData={structuredData}
      />
      <Navigation />
      <WhatsAppButton />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-foreground overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-10 w-72 h-72 border border-background/5 rounded-full" />
            <div className="absolute bottom-10 left-20 w-96 h-96 border border-background/5 rotate-12" />
          </div>
          <div className="container mx-auto relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold tracking-wider uppercase text-background/50 mb-4">
                Fugenlose Bäder & Oberflächen
              </span>
              <h1 className="font-caslon text-3xl md:text-4xl lg:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">
                Fugenloses Design: Ihr Badezimmer als moderne Wellness-Oase
              </h1>
              <p className="text-lg md:text-xl text-background/80 mb-10 leading-relaxed max-w-2xl">
                Verabschieden Sie sich von alten Fliesen und schmutzigen Fugen. Wir gestalten Ihr Bad 
                und Ihre Wohnräume in Herford mit exklusiven Spachteltechniken und wasserfesten Designoberflächen.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl transition-all duration-500 hover:scale-105 group"
                  onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Jetzt kostenlose Design-Beratung anfordern
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <a href="tel:+4952213832130">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                    <Phone className="mr-2 h-5 w-5" />
                    05221 / 3832130
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Das Konzept */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto max-w-4xl">
            <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
              Das Konzept
            </span>
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-8">
              Warum fugenlos? Das Badezimmer der nächsten Generation
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Ein Badezimmer ohne Fugen bietet nicht nur eine beeindruckende, großzügige Optik, sondern ist 
              auch unschlagbar pflegeleicht. Wo keine Fugen sind, können sich weder Kalk noch Schimmel festsetzen. 
              Mit unseren wasserfesten Beschichtungssystemen schaffen wir Oberflächen wie aus einem Guss – 
              individuell auf Ihren Stil in Herford abgestimmt.
            </p>
          </div>
        </section>

        {/* Design-Highlights */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Unsere Design-Highlights
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Unsere Design-Highlights für Ihr Zuhause
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {designHighlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-background rounded-3xl p-8 md:p-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-foreground flex items-center justify-center">
                        <Icon className="w-7 h-7 text-background" />
                      </div>
                      <h3 className="font-caslon text-xl font-bold text-foreground">{item.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-4">{item.text}</p>
                    <ul className="space-y-2">
                      {item.details.map((detail, i) => (
                        <li key={i} className="text-sm text-foreground font-medium flex items-start gap-2">
                          <span className="text-foreground mt-1">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Handwerkskunst / Stein-Standard */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-background/50 mb-4 block">
                Technik & Handwerk
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold">
                Handwerkskunst für höchste Ansprüche
              </h2>
              <p className="text-lg text-background/70 mt-6 leading-relaxed">
                Exklusive Oberflächen verzeihen keine Fehler. Als Fachbetrieb achten wir penibel auf:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-background/10 rounded-3xl overflow-hidden">
              {qualityPoints.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-foreground p-10 flex flex-col gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-background/10 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-background" />
                    </div>
                    <h3 className="font-caslon text-xl font-bold text-background">{item.title}</h3>
                    <p className="text-background/60 leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Häufige Fragen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Alles, was Sie wissen müssen
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="bg-background border border-foreground/10 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground">
          <div className="container mx-auto text-center max-w-3xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-background mb-6">
              Machen Sie Ihr Bad zum Unikat
            </h2>
            <p className="text-lg text-background/80 mb-10 leading-relaxed">
              Besuchen Sie uns oder vereinbaren Sie einen Termin. Wir bringen Musterplatten mit zu Ihnen 
              nach Herford, damit Sie die Oberflächen fühlen und im eigenen Licht sehen können.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl"
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Jetzt Beratungstermin vereinbaren
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+4952213832130">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                  <Phone className="mr-2 h-5 w-5" />
                  05221 / 3832130
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Kontaktformular */}
        <Contact />
      </main>
      
      <Footer />
    </div>
  );
};

export default Badsanierung;
